/* Anera — shared script
   Used by the static HTML pages AND the Shopify theme (shopify/assets/anera.js
   is an exact copy of this file — edit here, then copy). */
(function () {
  'use strict';

  var header = document.querySelector('.site-header');
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var inThemeEditor = !!(window.Shopify && window.Shopify.designMode);

  // ---------- Placeholder links ----------
  // A link with an empty href or just "#" (no destination set yet) does nothing when
  // clicked: no jump to the top of the page, no "#" added to the address. Other click
  // handlers on the link (menus, toggles) still run.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href').trim();
    if (href === '' || href === '#') e.preventDefault();
  });

  // ---------- Smooth scrolling (Lenis) ----------
  // Eased, inertial scrolling for mouse wheels and trackpads; anchor links
  // (e.g. #stores) glide to their target. The library is only downloaded on
  // devices with a mouse/trackpad — touch screens keep native scrolling and
  // never fetch it. Skipped for reduced-motion users and in the theme editor.
  // The script URL comes from data-lenis-src on this <script> tag.
  var lenis = null;
  var lenisSrc = document.currentScript && document.currentScript.getAttribute('data-lenis-src');

  function startSmoothScroll() {
    lenis = new window.Lenis({
      duration: 1.2,                                                  // seconds to settle
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); }, // ease-out-expo
      smoothWheel: true,
      wheelMultiplier: 1,
      anchors: true,
      autoRaf: true,
      allowNestedScroll: true
    });
    if (document.documentElement.classList.contains('anera-menu-open')) lenis.stop();
  }

  // Only on Anera pages (<body class="anera">): Atelier's own pages have drawers
  // and modals that manage scrolling themselves.
  var isAneraPage = document.body.classList.contains('anera');
  if (lenisSrc && isAneraPage && canHover.matches && !reducedMotion && !inThemeEditor) {
    if (window.Lenis) {
      startSmoothScroll();
    } else {
      var lenisScript = document.createElement('script');
      lenisScript.src = lenisSrc;
      lenisScript.async = true;
      lenisScript.onload = startSmoothScroll;
      document.head.appendChild(lenisScript);
    }
  }

  // ---------- Links to a section on this page (e.g. the hero's "/#visit") ----------
  // Glide to the section instead of jumping (or reloading for "/#id" links)
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href*="#"]');
    if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
    var url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || !url.hash || url.hash === '#') return;
    var target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    if (lenis) { lenis.scrollTo(target); } else { target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' }); }
    history.replaceState(null, '', url.hash);
  });

  // ---------- Announcement bar: scroll only when the message doesn't fit ----------
  // If the text is wider than the bar minus a 50px offset, the bar switches to a
  // right → left ticker (.announcement--scrolling); otherwise it stays centred.
  // Re-checked on resize and once web fonts load. Never scrolls for reduced motion.
  var ANNOUNCEMENT_OFFSET = 50;   // px of breathing room the text needs to count as "fits"
  var TICKER_SPEED = 40;          // px per second

  document.querySelectorAll('.announcement').forEach(function (bar) {
    var track = bar.querySelector('.announcement__track');
    var message = track && track.querySelector('p');
    if (!message) return;

    function update() {
      bar.classList.remove('announcement--scrolling');
      var textWidth = message.scrollWidth - parseFloat(getComputedStyle(message).paddingLeft) - parseFloat(getComputedStyle(message).paddingRight);
      var fits = textWidth <= bar.clientWidth - ANNOUNCEMENT_OFFSET;
      if (fits || reducedMotion) return;
      bar.classList.add('announcement--scrolling');
      // The track holds 4 copies and moves by half its width, so the loop is seamless
      var distance = track.scrollWidth / 2;
      track.style.setProperty('--anera-ticker-duration', (distance / TICKER_SPEED).toFixed(2) + 's');
    }

    update();
    var resizeTimer;
    window.addEventListener('resize', function () { clearTimeout(resizeTimer); resizeTimer = setTimeout(update, 150); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
  });

  // ---------- Pinned header: hides on scroll down, slides back in on scroll up ----------
  // Once the header has scrolled out of view, its bar is fixed to the top of the
  // screen (hidden). Scrolling up slides it down, scrolling down slides it away,
  // and back at the top it returns to its normal place. It stays put while the
  // mega menu or mobile menu is open, and shows when a keyboard user tabs into it.
  var headerBar = header && header.querySelector('.site-header__inner');
  if (headerBar) {
    var SCROLL_TOLERANCE = 6;     // px of movement before a direction counts (ignores tiny jitters)
    var lastScrollY = window.scrollY;
    var headerTicking = false;

    var setPinnedVisible = function (visible) { header.classList.toggle('is-pinned-visible', visible); };
    // Back at the top the fixed bar hands over to the normal header. If the bar is on
    // screen, glide it (FLIP): start from where / how the fixed bar looked, then ease
    // its position, height, background and shadow into the normal header.
    var UNPIN_EASE = '.5s cubic-bezier(.22, 1, .36, 1)';
    var unpinHeader = function () {
      var wasVisible = header.classList.contains('is-pinned-visible');
      var before = wasVisible && !reducedMotion ? headerBar.getBoundingClientRect() : null;
      var beforeStyle = before ? getComputedStyle(headerBar) : null;
      var fromBackground = before ? beforeStyle.backgroundColor : '';
      var fromShadow = before ? beforeStyle.boxShadow : '';
      header.classList.remove('is-pinned', 'is-pinned-visible');
      header.style.minHeight = '';
      if (!before) return;

      var after = headerBar.getBoundingClientRect();
      var dy = before.top - after.top;
      if (Math.abs(dy) < 1 && Math.abs(before.height - after.height) < 1) return;
      headerBar.style.transition = 'none';
      headerBar.style.transform = 'translate3d(0, ' + dy + 'px, 0)';
      headerBar.style.height = before.height + 'px';
      headerBar.style.backgroundColor = fromBackground;
      headerBar.style.boxShadow = fromShadow;
      void headerBar.offsetWidth; // start from the "fixed bar" look
      headerBar.style.transition = ['transform', 'height', 'background-color', 'box-shadow'].map(function (p) { return p + ' ' + UNPIN_EASE; }).join(', ');
      headerBar.style.transform = '';
      headerBar.style.height = '';
      headerBar.style.backgroundColor = '';
      headerBar.style.boxShadow = '';
      var cleanUp = function (e) {
        if (e && e.target !== headerBar) return;
        headerBar.style.transition = '';
        headerBar.removeEventListener('transitionend', cleanUp);
      };
      headerBar.addEventListener('transitionend', cleanUp);
      setTimeout(cleanUp, 700);
    };
    var headerIsBusy = function () {
      return header.classList.contains('is-mega-open') || document.documentElement.classList.contains('anera-menu-open');
    };

    var updateHeader = function () {
      headerTicking = false;
      var y = window.scrollY;
      var rect = header.getBoundingClientRect();
      var headerTop = rect.top + y;       // where the header normally sits
      var headerBottom = rect.bottom + y;
      var pinned = header.classList.contains('is-pinned');
      var visible = header.classList.contains('is-pinned-visible');

      if (y <= headerTop || (y <= headerBottom && !visible)) {
        if (pinned) unpinHeader();
        lastScrollY = y;
        return;
      }
      if (y <= headerBottom) return;      // visible bar over the header's own spot: keep it

      if (!pinned) {
        header.style.minHeight = header.offsetHeight + 'px'; // keep its space so the page doesn't jump
        header.classList.add('is-pinned');
      }
      var delta = y - lastScrollY;
      if (Math.abs(delta) < SCROLL_TOLERANCE) return;
      lastScrollY = y;
      if (delta < 0) setPinnedVisible(true);
      else if (!headerIsBusy()) setPinnedVisible(false);
    };
    var requestHeaderUpdate = function () {
      if (!headerTicking) { headerTicking = true; requestAnimationFrame(updateHeader); }
    };

    window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
    window.addEventListener('resize', requestHeaderUpdate);
    header.addEventListener('focusin', function () {
      if (header.classList.contains('is-pinned')) setPinnedVisible(true);
    });
    updateHeader();
  }

  // ---------- Simple dropdown: toggle on click/tap (hover handled in CSS) ----------
  var dropdownButtons = document.querySelectorAll('.has-dropdown > .main-nav__link');
  dropdownButtons.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      var open = btn.getAttribute('aria-expanded') === 'true';
      // A parent that is a link: with a mouse (dropdown already open on hover) or on a
      // second tap it goes to its page; the first tap on a touch screen opens the dropdown
      if (btn.tagName === 'A' && (canHover.matches || open)) return;
      if (btn.tagName === 'A') e.preventDefault();
      btn.setAttribute('aria-expanded', String(!open));
    });
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.has-dropdown')) {
      dropdownButtons.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    }
  });

  // ---------- Mega menu ----------
  // The panel stays display:none (so its images don't download) until the
  // visitor first moves into, or tabs into, the header.
  function prepareMega() {
    if (!header || header.classList.contains('is-mega-ready')) return;
    header.classList.add('is-mega-ready');
    // Stagger order for the open animation: category names, and the images in each category
    header.querySelectorAll('.mega__list li').forEach(function (li, i) { li.style.setProperty('--anera-mega-index', i); });
    header.querySelectorAll('.mega__panel').forEach(function (panel) {
      panel.querySelectorAll('.mega__card').forEach(function (card, i) {
        var media = card.querySelector('img, svg');
        if (media) media.style.setProperty('--anera-mega-index', i);
      });
    });
    void header.offsetWidth; // apply display before the open transition starts
  }
  // Once the panel has slid open, switching category shows its images without waiting
  var megaSettleTimer = null;
  function setMegaOpen(open) {
    clearTimeout(megaSettleTimer);
    header.classList.toggle('is-mega-open', open);
    if (open) {
      megaSettleTimer = setTimeout(function () { header.classList.add('is-mega-settled'); }, 600);
    } else {
      header.classList.remove('is-mega-settled');
    }
  }
  if (header) {
    header.addEventListener('pointerenter', prepareMega, { once: true });
    header.addEventListener('focusin', prepareMega, { once: true });
  }

  // Show the image group that belongs to category `index` (0-based) and highlight its link
  function showMegaCategory(mega, index) {
    mega.querySelectorAll('[data-mega-target]').forEach(function (link) {
      link.classList.toggle('is-active', link.getAttribute('data-mega-target') === String(index));
    });
    mega.querySelectorAll('[data-mega-panel]').forEach(function (panel) {
      panel.classList.toggle('is-active', panel.getAttribute('data-mega-panel') === String(index));
    });
  }

  document.querySelectorAll('.has-mega').forEach(function (item) {
    var btn = item.querySelector('.main-nav__link');
    var mega = item.querySelector('.mega');
    var closeTimer = null;
    var openScrollY = 0;

    // Hovering (or tabbing to) a category swaps the images on the right
    item.querySelectorAll('[data-mega-target]').forEach(function (link) {
      var show = function () { showMegaCategory(mega, link.getAttribute('data-mega-target')); };
      link.addEventListener('mouseenter', show);
      link.addEventListener('focus', show);
    });

    function open() {
      clearTimeout(closeTimer);
      prepareMega();
      if (!isOpen()) { setMegaOpen(true); openScrollY = window.scrollY; }
      btn.setAttribute('aria-expanded', 'true');
    }
    function close() {
      clearTimeout(closeTimer);
      setMegaOpen(false);
      btn.setAttribute('aria-expanded', 'false');
      if (mega) showMegaCategory(mega, 0); // next time it opens on the first category
    }
    function isOpen() { return header.classList.contains('is-mega-open'); }

    item.addEventListener('mouseenter', function () { if (canHover.matches) open(); });
    item.addEventListener('mouseleave', function () {
      if (canHover.matches) closeTimer = setTimeout(close, 180);
    });
    btn.addEventListener('click', function (e) {
      // A parent that is a link (e.g. Jewellery -> /collections/all): a mouse click goes to its
      // page (the panel is already open from hover); on a touch screen the first tap opens the
      // panel and a second tap goes to the page.
      if (btn.tagName === 'A') {
        if (canHover.matches || isOpen()) return;
        e.preventDefault();
        open();
        return;
      }
      // A plain button: with a mouse the panel is already open from hover, so a click keeps it open.
      if (canHover.matches) { open(); } else if (isOpen()) { close(); } else { open(); }
    });
    // Keyboard: the arrow down key opens the panel and moves into its categories
    btn.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown') return;
      e.preventDefault();
      open();
      var firstCategory = mega && mega.querySelector('[data-mega-target]');
      if (firstCategory) firstCategory.focus();
    });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) close();
    });
    document.addEventListener('click', function (e) {
      if (isOpen() && !item.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) { close(); btn.focus(); }
    });
    // Scrolling the page closes it (it would otherwise hang over the content)
    window.addEventListener('scroll', function () {
      if (isOpen() && !inThemeEditor && Math.abs(window.scrollY - openScrollY) > 30) close();
    }, { passive: true });
  });

  // Shopify theme editor: selecting a mega menu block opens the menu on that category
  document.addEventListener('shopify:block:select', function (e) {
    var panel = e.target.closest && e.target.closest('[data-mega-panel]');
    if (!header || !panel) return;
    prepareMega();
    setMegaOpen(true);
    showMegaCategory(panel.closest('.mega'), panel.getAttribute('data-mega-panel'));
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    if (header && e.target.closest && e.target.closest('[data-mega-panel]')) setMegaOpen(false);
  });

  // ---------- Collection list: hovering a category shows its image ----------
  // Same idea as the mega menu: the image of the hovered (or focused) category
  // crossfades in and its link turns dark. The last one hovered stays shown.
  document.querySelectorAll('[data-moments]').forEach(function (section) {
    var links = section.querySelectorAll('[data-moments-target]');
    var slides = section.querySelectorAll('[data-moments-slide]');
    if (slides.length < 2) return;

    function showMoment(index) {
      index = String(index);
      links.forEach(function (link) { link.classList.toggle('is-active', link.getAttribute('data-moments-target') === index); });
      slides.forEach(function (slide) { slide.classList.toggle('is-active', slide.getAttribute('data-moments-slide') === index); });
    }

    links.forEach(function (link) {
      var show = function () {
        if (!link.classList.contains('is-active')) showMoment(link.getAttribute('data-moments-target'));
      };
      link.addEventListener('mouseenter', show);
      link.addEventListener('focus', show);
    });

    // Theme editor: selecting a category block shows its image
    section.addEventListener('shopify:block:select', function (e) {
      var item = e.target.closest && e.target.closest('[data-moments-index]');
      if (item) showMoment(item.getAttribute('data-moments-index'));
    });
  });

  // ---------- Mobile menu drawer ----------
  var menu = document.getElementById('mobile-menu');
  var toggles = document.querySelectorAll('[data-menu-toggle]');
  var lastFocus = null;

  function setMenu(open) {
    if (!menu) return;
    menu.classList.toggle('is-open', open);
    menu.setAttribute('aria-hidden', String(!open));
    if (open) { menu.removeAttribute('inert'); } else { menu.setAttribute('inert', ''); }
    document.documentElement.classList.toggle('anera-menu-open', open);
    if (lenis) { if (open) { lenis.stop(); } else { lenis.start(); } }
    toggles.forEach(function (t) { t.setAttribute('aria-expanded', String(open)); });

    if (open) {
      lastFocus = document.activeElement;
      var first = menu.querySelector('.mobile-menu__close');
      if (first) first.focus();
    } else if (lastFocus) {
      lastFocus.focus();
    }
  }

  if (menu) {
    // Stagger order for the open animation (menu items, and the links in each sub-menu)
    var menuItems = menu.querySelectorAll('.mobile-menu__nav > ul > li');
    menuItems.forEach(function (li, i) { li.style.setProperty('--anera-menu-index', i); });
    menu.style.setProperty('--anera-menu-count', menuItems.length);
    menu.querySelectorAll('.mobile-menu__sub').forEach(function (sub) {
      sub.querySelectorAll('li').forEach(function (li, i) { li.style.setProperty('--anera-menu-index', i); });
    });

    toggles.forEach(function (t) {
      t.addEventListener('click', function () { setMenu(!menu.classList.contains('is-open')); });
    });
    menu.querySelectorAll('[data-menu-close]').forEach(function (el) {
      el.addEventListener('click', function () { setMenu(false); });
    });
    menu.addEventListener('click', function (e) {
      if (e.target.closest('a[href]')) setMenu(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) setMenu(false);
    });
    // Close the drawer if the viewport grows to desktop while it's open
    var desktop = window.matchMedia('(min-width: 901px)');
    var onChange = function (mq) { if (mq.matches) setMenu(false); };
    if (desktop.addEventListener) desktop.addEventListener('change', onChange);
    else if (desktop.addListener) desktop.addListener(onChange);
  }

  // ---------- Hero background video ----------
  // The poster image shows until the video's first frame is drawn (the video is never
  // hidden — iOS Safari won't autoplay a video it treats as invisible).
  // Phones only autoplay a video that is muted and inline, so that's enforced here too.
  // If autoplay is blocked (iPhone Low Power Mode, Android Data Saver), the video starts
  // on the visitor's first tap / click instead. Visitors who prefer reduced motion keep
  // the still image only when the hero asks for it (data-respect-reduced-motion).
  // An image banner's background video (.intention__video) works the same way, but only
  // starts loading and playing once the banner comes near the screen.
  document.querySelectorAll('.page-hero__video, .intention__video').forEach(function (video) {
    var hero = video.closest('.page-hero, .intention');
    if (reducedMotion && hero && hero.hasAttribute('data-respect-reduced-motion')) {
      video.removeAttribute('autoplay');
      video.pause();
      return;
    }
    if (video.classList.contains('intention__video') && 'IntersectionObserver' in window) {
      var nearScreen = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        nearScreen.disconnect();
        startBackgroundVideo(video, hero);
      }, { rootMargin: '400px 0px' });
      nearScreen.observe(hero);
      return;
    }
    startBackgroundVideo(video, hero);
  });

  // Gives a video the section's own image as its poster (the exact file the <picture>
  // picked for this screen, so nothing extra is downloaded). The video element then
  // shows that image itself until its first frame is ready, so there is never an empty
  // or black moment between the image and the video.
  function usePosterImage(video, img) {
    if (!video || !img) return;
    var apply = function () {
      var src = img.currentSrc || img.src;
      if (src && video.getAttribute('poster') !== src) video.setAttribute('poster', src);
    };
    if (img.complete && img.naturalWidth) apply();
    else img.addEventListener('load', apply, { once: true });
  }

  function startBackgroundVideo(video, hero) {
    usePosterImage(video, hero && hero.querySelector('.page-hero__media img, .intention__bg img'));

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    // The poster stays on top until the video has actually drawn a frame, then fades out.
    // ("playing" alone can fire a moment before the first frame is on screen, which showed
    // as a blank flash between the image and the video.)
    var revealed = false;
    var reveal = function () {
      if (revealed) return;
      revealed = true;
      video.classList.add('is-ready');
      if (hero) hero.classList.add('is-video-playing');
    };
    var revealOnFrame = function () {
      if (revealed) return;
      // a frame has been put on screen…
      if (video.requestVideoFrameCallback) {
        video.requestVideoFrameCallback(function () { requestAnimationFrame(reveal); });
      }
      // …or, where the browser doesn't report frames, playback has measurably moved on
      var onTime = function () {
        if (!revealed && video.currentTime < .1) return;
        video.removeEventListener('timeupdate', onTime);
        reveal();
      };
      video.addEventListener('timeupdate', onTime);
    };
    video.addEventListener('playing', revealOnFrame);
    if (!video.paused && video.readyState >= 3) revealOnFrame();

    function tryPlay() {
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {}); // blocked → poster stays for now
    }
    tryPlay();
    // iOS can refuse a play() made before the video has data: try again once it can play
    video.addEventListener('canplay', function () { if (video.paused) tryPlay(); }, { once: true });
    if (video.readyState === 0 && video.preload === 'none') video.preload = 'auto';

    // Autoplay blocked? Start on the first user gesture (these count as permission to play)
    var gestures = ['touchend', 'click', 'keydown'];
    var onGesture = function () {
      if (video.paused) tryPlay();
      gestures.forEach(function (g) { window.removeEventListener(g, onGesture, true); });
    };
    gestures.forEach(function (g) { window.addEventListener(g, onGesture, { capture: true, passive: true }); });

    // Phones pause background video when the tab or app is hidden; resume on return
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && video.paused) tryPlay();
    });
  }

  // ---------- Video banner: click to play in place, click again to pause ----------
  document.querySelectorAll('[data-video-banner]').forEach(function (banner) {
    var media = banner.querySelector('.video-banner__media');
    var btn = banner.querySelector('[data-video-play]');
    var video = banner.querySelector('.video-banner__video');
    var tpl = banner.querySelector('template[data-video-template]'); // YouTube / Vimeo
    if (!media || (!video && !tpl)) return;
    banner.classList.add('has-video');
    usePosterImage(video, media.querySelector('.video-banner__poster img, :scope > img'));

    function setPlaying(playing) {
      banner.classList.toggle('is-playing', playing);
      if (btn) btn.setAttribute('aria-label', playing ? btn.dataset.labelPause : btn.dataset.labelPlay);
    }

    if (video) {
      video.addEventListener('play', function () { banner.classList.add('has-started'); setPlaying(true); });
      video.addEventListener('pause', function () { setPlaying(false); });
      video.addEventListener('ended', function () { setPlaying(false); });
    }

    function toggle() {
      if (video) {
        if (video.paused || video.ended) {
          var attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(function () {});
        } else {
          video.pause();
        }
        return;
      }
      // External player: load it once; from then on it has its own controls
      if (!banner.classList.contains('is-playing')) {
        var player = document.createElement('div');
        player.className = 'video-banner__player';
        player.appendChild(tpl.content.cloneNode(true));
        media.appendChild(player);
        banner.classList.add('is-external', 'has-started');
        setPlaying(true);
      }
    }

    // Autoplay (on by default). Behind the scenes, to keep the page light, the video is not
    // loaded with the page: it starts, muted, once the banner is within 200px of the screen
    // (data-autoplay-offset overrides that), pauses when it scrolls away and resumes on return.
    // A visitor's own pause is respected. If the device blocks autoplay (or the theme
    // editor), the play button shows instead. Like the hero video, it also plays for
    // visitors with "Reduce motion" on (common on phones), unless the banner asks for
    // the still image then (data-respect-reduced-motion).
    var autoplay = video && banner.hasAttribute('data-autoplay');
    var userPaused = false;
    var autoPaused = false;
    if (autoplay) {
      var keepStill = reducedMotion && banner.hasAttribute('data-respect-reduced-motion');
      if (keepStill || inThemeEditor || !('IntersectionObserver' in window)) {
        banner.classList.add('is-autoplay-blocked');
      } else {
        // Phones only autoplay a video that is muted and inline
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        var inView = false;
        var autoStart = function () {
          if (!inView || userPaused || !video.paused) return;
          if (video.preload === 'none') video.preload = 'auto';
          autoPaused = false;
          var attempt = video.play();
          if (attempt && attempt.catch) {
            attempt.catch(function (error) {
              if (error && error.name === 'AbortError') return;   // our own pause (scrolled away) cut it short
              banner.classList.add('is-autoplay-blocked');         // play button shows meanwhile
              // iOS can refuse a play() made before the video has data: try once more when it can play
              if (video.readyState < 3) {
                video.addEventListener('canplay', autoStart, { once: true });
                if (video.readyState === 0) video.load();
              }
            });
          }
        };
        video.addEventListener('playing', function () { banner.classList.remove('is-autoplay-blocked'); });
        // Still blocked (iPhone Low Power Mode, Android Data Saver)? Any tap or key press
        // counts as permission, so the video starts on the visitor's first one.
        ['touchend', 'click', 'keydown'].forEach(function (gesture) {
          window.addEventListener(gesture, function () {
            if (banner.classList.contains('is-autoplay-blocked')) autoStart();
          }, { capture: true, passive: true });
        });

        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
            if (inView) {
              autoStart();
            } else if (!video.paused) {
              autoPaused = true;
              video.pause();
            }
          });
        }, { rootMargin: '0px 0px ' + (banner.hasAttribute('data-autoplay-offset') ? parseInt(banner.getAttribute('data-autoplay-offset'), 10) || 0 : 200) + 'px 0px' }).observe(media);
      }
    }

    media.addEventListener('click', function (e) {
      if (e.target.closest('.video-banner__player')) return;
      // Autoplaying banners can't be paused by a click; a click only starts the video
      // when autoplay was blocked (play button showing)
      if (autoplay && !(video.paused || video.ended)) return;
      if (video) userPaused = !(video.paused || video.ended);   // clicking pauses a playing video
      toggle();
    });
    if (autoplay) {
      // A video without loop that has finished stays finished (no restart on scroll)
      video.addEventListener('ended', function () { userPaused = true; });
    }
  });

  // ---------- Image carousel (looping) ----------
  document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
    var track = gallery.querySelector('.gallery__track');
    var originals = Array.prototype.slice.call(track.querySelectorAll('.gallery__slide'));
    var dots = gallery.querySelectorAll('.gallery__dots button');
    var count = originals.length;
    if (count < 2) return;

    // Clone the slides before and after so the loop never runs out of neighbours
    function cloneAll() {
      return originals.map(function (slide) {
        var c = slide.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.querySelectorAll('a, button').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
        c.removeAttribute('data-shopify-editor-block');
        return c;
      });
    }
    cloneAll().forEach(function (c) { track.insertBefore(c, originals[0]); });
    cloneAll().forEach(function (c) { track.appendChild(c); });
    var slides = Array.prototype.slice.call(track.children);

    var index = Math.min(Math.max(parseInt(gallery.dataset.start || '0', 10) || 0, 0), count - 1);
    var offset = 0;          // current translateX in px
    var autoplayMs = parseInt(gallery.dataset.autoplay || '0', 10);
    var timer = null;

    function slideCentre(i) {
      var s = slides[i + count];                      // real slide i sits after the first set of clones
      return s.offsetLeft + s.offsetWidth / 2;
    }
    function targetOffset(i) { return gallery.clientWidth / 2 - slideCentre(i); }
    function apply(x, animate) {
      offset = x;
      track.classList.toggle('is-animating', !!animate);
      track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }
    function mark() {
      var real = ((index % count) + count) % count;
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === real + count); });
      dots.forEach(function (d, i) { d.setAttribute('aria-current', String(i === real)); });
    }
    function goTo(i, animate) {
      index = i;
      apply(targetOffset(i), animate !== false);
      mark();
    }
    // After animating onto a clone, jump (invisibly) to the matching real slide
    track.addEventListener('transitionend', function (e) {
      if (e.target !== track) return;
      track.classList.remove('is-animating');
      if (index < 0 || index >= count) {
        index = ((index % count) + count) % count;
        apply(targetOffset(index), false);
        mark();
      }
    });

    function next() { goTo(index + 1); }
    function prev() { goTo(index - 1); }

    // Autoplay (pauses on hover, focus, drag, hidden tab; off for reduced motion)
    function stop() { clearInterval(timer); timer = null; }
    function start() {
      stop();
      if (autoplayMs > 0 && !reducedMotion && !inThemeEditor) timer = setInterval(next, autoplayMs);
    }
    gallery.addEventListener('mouseenter', stop);
    gallery.addEventListener('mouseleave', start);
    gallery.addEventListener('focusin', stop);
    gallery.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () { if (document.hidden) { stop(); } else { start(); } });

    // Drag / swipe
    var startX = 0, startY = 0, startOffset = 0, dragging = false, moved = false, horizontal = null;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true; moved = false; horizontal = null;
      startX = e.clientX; startY = e.clientY; startOffset = offset;
      track.classList.remove('is-animating');
      stop();
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (horizontal === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) horizontal = Math.abs(dx) > Math.abs(dy);
      if (!horizontal) return;
      moved = true;
      track.classList.add('is-dragging');
      apply(startOffset + dx, false);
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (moved) {
        var dx = e.clientX - startX;
        var threshold = Math.min(120, slides[count].offsetWidth * 0.15);
        if (dx < -threshold) { next(); } else if (dx > threshold) { prev(); } else { goTo(index); }
      }
      start();
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    // Click a peeking slide to bring it to the centre
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); moved = false; return; }
      var slide = e.target.closest('.gallery__slide');
      if (!slide || slide.classList.contains('is-active')) return;
      e.preventDefault();
      goTo(slides.indexOf(slide) - count);
    });

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { goTo(i); }); });

    window.addEventListener('resize', function () { goTo(index, false); });
    window.addEventListener('load', function () { goTo(index, false); });
    goTo(index, false);
    start();
  });

  // ---------- Newsletter forms: message only on the form that was used ----------
  // Shopify reloads the page after a sign-up and every customer form on it sees the
  // same "posted" state, so both the newsletter section and the footer would show
  // the thank-you / error message. Remember which form was submitted (for that one
  // reload), keep its message, hide the others, and scroll the used form into view.
  var NEWSLETTER_KEY = 'anera-newsletter-submitted';
  var newsletterForms = document.querySelectorAll('form.anera-newsletter');

  // Loader: on submit the button shows a spinner and is disabled (no double sign-ups).
  // Only fires for a valid email: the browser blocks "submit" for invalid input.
  function setNewsletterLoading(form, loading) {
    var button = form.querySelector('button[type="submit"]');
    form.classList.toggle('is-loading', loading);
    form.setAttribute('aria-busy', String(loading));
    if (button) button.disabled = loading;
  }

  newsletterForms.forEach(function (form) {
    form.addEventListener('submit', function () {
      // Consent checkbox ticked: tag the customer, so the WhatsApp / SMS consent is on record
      var consent = form.querySelector('[data-consent-tag]');
      var tags = form.querySelector('input[name="contact[tags]"]');
      if (consent && consent.checked && tags && tags.value.indexOf(consent.dataset.consentTag) === -1) {
        tags.value += ',' + consent.dataset.consentTag;
      }
      try { sessionStorage.setItem(NEWSLETTER_KEY, form.id); } catch (e) { /* storage blocked: all messages stay visible */ }
      // The static preview pages post to "#" (nowhere), so they get no loader
      if (form.getAttribute('action') !== '#') setNewsletterLoading(form, true);
    });
  });

  // Back button can restore the page from memory with the loader still on: clear it
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) newsletterForms.forEach(function (form) { setNewsletterLoading(form, false); });
  });

  var submittedFormId = null;
  try {
    submittedFormId = sessionStorage.getItem(NEWSLETTER_KEY);
    sessionStorage.removeItem(NEWSLETTER_KEY);
  } catch (e) { /* ignore */ }

  if (submittedFormId && document.getElementById(submittedFormId)) {
    newsletterForms.forEach(function (form) {
      if (form.id !== submittedFormId) {
        form.querySelectorAll('.form-message').forEach(function (msg) { msg.hidden = true; });
      }
    });
    var usedForm = document.getElementById(submittedFormId);
    if (usedForm.querySelector('.form-message')) {
      usedForm.scrollIntoView({ block: 'center' });
    }
  }

  // ---------- Lead form ("Join the Anera community") ----------
  // A full-screen form opened by any link to #lead-form (the catalogue's "Get in touch"),
  // or by visiting the page with #lead-form in the address. On submit:
  //   1. the details are sent to a Google Sheet: the Apps Script web app in data-sheet-url
  //      (google-apps-script/ in the repo; posted as text/plain, like the Cohorts form,
  //      because Apps Script can't answer a CORS preflight);
  //   2. on Shopify the form is then posted as a customer form, which saves the customer
  //      with the "lead" tag (and the WhatsApp / SMS consent tag). Shopify reloads the
  //      page, and the form reopens on its thank-you (or error) message.
  // The static preview pages have no Shopify, so they show the thank-you message directly.
  var leadModal = document.querySelector('[data-lead-modal]');
  if (leadModal) {
    var leadForm = leadModal.querySelector('form');
    var leadSubmit = leadForm.querySelector('[type="submit"]');
    var leadLastFocus = null;
    var LEAD_HASH = '#' + leadModal.id;

    var openLead = function (done) {
      leadLastFocus = document.activeElement;
      leadModal.classList.toggle('is-done', !!done);
      leadModal.removeAttribute('inert');
      leadModal.setAttribute('aria-hidden', 'false');
      leadModal.classList.add('is-open');
      document.documentElement.classList.add('anera-modal-open');
      if (lenis) lenis.stop();
      var target = done ? leadModal.querySelector('.lead-modal__done .lead-modal__title') : leadForm.querySelector('input:not([type="hidden"])');
      if (target) {
        if (done) target.setAttribute('tabindex', '-1');
        setTimeout(function () { target.focus({ preventScroll: true }); }, 50);
      }
    };
    var closeLead = function () {
      leadModal.classList.remove('is-open');
      leadModal.setAttribute('aria-hidden', 'true');
      leadModal.setAttribute('inert', '');
      document.documentElement.classList.remove('anera-modal-open');
      if (lenis) lenis.start();
      if (leadModal.classList.contains('is-done')) {
        leadModal.classList.remove('is-done');
        leadForm.reset();
      }
      if (typeof clearLeadErrors === 'function') clearLeadErrors();
      if (location.hash === LEAD_HASH) history.replaceState(null, '', location.pathname + location.search);
      if (leadLastFocus && leadLastFocus.focus) leadLastFocus.focus({ preventScroll: true });
    };

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('a[href$="' + LEAD_HASH + '"], [data-lead-open]');
      if (trigger) { e.preventDefault(); openLead(false); return; }
      if (e.target.closest('[data-lead-close]')) closeLead();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && leadModal.classList.contains('is-open')) closeLead();
    });
    // Theme editor: selecting the section shows the form
    document.addEventListener('shopify:section:select', function (e) { if (e.target.contains(leadModal)) openLead(false); });
    document.addEventListener('shopify:section:deselect', function (e) { if (e.target.contains(leadModal)) closeLead(); });

    // Returns the number as "+919876543210", or '' when it isn't a valid number.
    // India (with or without +91 / 91 / 0): a 10-digit mobile starting 6-9.
    // Other countries: "+", the country code and the number, 10-15 digits in all (+1: exactly 11).
    // "98765 43210", "098765 43210", "+91 98765-43210", "0091 98765 43210" all become "+919876543210".
    var normalisePhone = function (raw) {
      var v = String(raw || '').trim();
      if (/[^\d\s\-().+]/.test(v) || v.indexOf('+', 1) !== -1) return '';   // letters, or a "+" in the middle
      v = v.replace(/[\s\-().]/g, '');
      if (/^00/.test(v)) v = '+' + v.slice(2);
      if (/^\+91/.test(v)) v = v.slice(3);
      else if (v.charAt(0) === '+') {
        if (/^\+1/.test(v)) return /^\+1[2-9]\d{9}$/.test(v) ? v : '';             // US / Canada: +1 and 10 digits
        return /^\+[2-9]\d{9,14}$/.test(v) && !/^\+(\d)\1+$/.test(v) ? v : '';    // elsewhere: 10-15 digits with the code
      }
      else if (/^91\d{10}$/.test(v)) v = v.slice(2);
      else v = v.replace(/^0/, '');
      return /^[6-9]\d{9}$/.test(v) ? '+91' + v : '';
    };

    var leadField = function (key) { return leadForm.querySelector('[data-lead-field="' + key + '"]'); };
    var phoneInput = leadField('phone');
    var leadConsent = leadForm.querySelector('[data-consent-tag]');
    var LEAD_ERRORS = {
      name: 'Please enter your name',
      phone: 'Please enter a valid phone number, e.g. +91 98765 43210',
      email: 'Please enter a valid email address',
      consent: 'Please tick the box to agree before submitting'
    };
    // The message under the consent box
    var LEAD_FORM_ERRORS = {
      invalid: 'Please fix the errors above before submitting.',
      consent: 'Please tick the box above to agree before submitting.',
      failed: 'Sorry, your details could not be sent. Please check your connection and try again.'
    };
    var leadMessage = leadForm.querySelector('.lead-form__message');
    var showLeadMessage = function (text) {
      if (!leadMessage) return;
      leadMessage.textContent = text;
      leadMessage.hidden = !text;
    };
    // Once every field is fine again, the "please fix" note goes away by itself
    var leadMessageIsValidation = false;
    var refreshLeadMessage = function () {
      if (!leadMessageIsValidation) return;
      var stillWrong = ['name', 'phone', 'email', 'consent'].some(function (key) { return fieldError(key, true); });
      if (!stillWrong) { leadMessageIsValidation = false; showLeadMessage(''); }
    };
    leadForm.noValidate = true;   // the messages below replace the browser's own pop-ups

    // An error replaces the field's label (in red) until the field is corrected
    var labelFor = function (input) { return input && leadForm.querySelector('label[for="' + input.id + '"]'); };
    var setFieldError = function (input, message) {
      var label = labelFor(input);
      if (!input) return;
      if (label && label.dataset.label === undefined) label.dataset.label = label.innerHTML;
      if (message) {
        input.setAttribute('aria-invalid', 'true');
        if (label) {
          if (input.type === 'checkbox') label.classList.add('is-error');
          else { label.textContent = message; label.classList.add('is-error'); }
        }
      } else {
        input.removeAttribute('aria-invalid');
        if (label && label.classList.contains('is-error')) {
          label.classList.remove('is-error');
          label.innerHTML = label.dataset.label;
        }
      }
    };
    // The error for one field, or '' when it's fine. "force": also flag an empty required field.
    var fieldError = function (key, force) {
      if (key === 'consent') return leadConsent && !leadConsent.checked ? LEAD_ERRORS.consent : '';
      var input = leadField(key);
      if (!input) return '';
      var v = input.value.trim();
      if (!v) return force && input.required ? LEAD_ERRORS[key] : '';
      if (key === 'phone') return normalisePhone(v) ? '' : LEAD_ERRORS.phone;
      if (key === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? '' : LEAD_ERRORS.email;
      return '';
    };
    var validateLead = function () {
      var first = null;
      var wrong = [];
      ['name', 'phone', 'email', 'consent'].forEach(function (key) {
        var input = key === 'consent' ? leadConsent : leadField(key);
        var message = fieldError(key, true);
        setFieldError(input, message);
        if (message) wrong.push(key);
        if (message && !first) first = input;
      });
      if (first) {
        // Only the tick box missing: say so; otherwise a general "please fix" note
        leadMessageIsValidation = true;
        showLeadMessage(wrong.length === 1 && wrong[0] === 'consent' ? LEAD_FORM_ERRORS.consent : LEAD_FORM_ERRORS.invalid);
        first.focus();
      }
      return !first;
    };
    var clearLeadErrors = function () {
      ['name', 'phone', 'email'].forEach(function (key) { setFieldError(leadField(key), ''); });
      setFieldError(leadConsent, '');
      leadMessageIsValidation = false;
      if (leadMessage && !leadMessage.hasAttribute('data-shopify-error')) showLeadMessage('');
    };
    ['name', 'phone', 'email'].forEach(function (key) {
      var input = leadField(key);
      if (!input) return;
      // Typing clears the error; leaving a filled-in field checks its format straight away
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid')) setFieldError(input, '');
        refreshLeadMessage();
      });
      input.addEventListener('blur', function () { setFieldError(input, fieldError(key, false)); });
    });
    if (leadConsent) leadConsent.addEventListener('change', function () {
      setFieldError(leadConsent, fieldError('consent'));
      refreshLeadMessage();
    });

    var setLeadLoading = function (loading) {
      leadForm.classList.toggle('is-loading', loading);
      leadForm.setAttribute('aria-busy', String(loading));
      if (leadSubmit) {
        if (!leadSubmit.dataset.label) leadSubmit.dataset.label = leadSubmit.textContent;
        leadSubmit.textContent = loading ? (leadSubmit.dataset.loadingLabel || 'Sending…') : leadSubmit.dataset.label;
      }
    };

    // Sends the lead to the Google Sheet. Resolves either way: on Shopify the customer
    // record is still saved, so a sheet hiccup must not lose the visitor's details there.
    var sendLeadToSheet = function (payload) {
      var url = leadForm.dataset.sheetUrl;
      if (!url) {
        console.info('[Anera] Lead form: no Google Sheet URL set, so the sheet was skipped.', payload);
        return Promise.resolve(false);
      }
      var controller = 'AbortController' in window ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, 10000) : null;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      })
        .then(function (res) { return res.json().catch(function () { return {}; }); })
        .then(function (data) {
          if (data && data.result === 'error') throw new Error(data.message || 'rejected');
          return true;
        })
        .catch(function (err) {
          console.warn('[Anera] Lead form: the Google Sheet did not accept the details.', err);
          return false;
        })
        .then(function (ok) { if (timer) clearTimeout(timer); return ok; });
    };

    // Two passes. 1st (the visitor's click): checked here first, in the capture phase; it never
    // goes further, so no other script can post the form unchecked. Once valid and saved to the
    // sheet, a 2nd submit is made on purpose (leadReady) and let through untouched, so Shopify's
    // own CAPTCHA script sees it, adds its token and posts the form. (Posting with form.submit()
    // skips that script and Shopify answers "Missing CAPTCHA token".)
    var leadSending = false;
    var leadReady = false;
    var LEAD_SHEET_KEY = 'anera-lead-sheet';
    leadForm.addEventListener('submit', function (e) {
      if (leadReady) { leadReady = false; return; }   // 2nd pass: Shopify takes it from here
      e.preventDefault();
      e.stopImmediatePropagation();
      if (leadSending || leadForm.classList.contains('is-loading')) return;
      if (leadMessage) leadMessage.removeAttribute('data-shopify-error');

      // Nothing is sent unless every field is valid (the phone number included)
      if (!validateLead()) return;
      var phone = phoneInput ? normalisePhone(phoneInput.value) : '';
      if (phoneInput && !phone) return;
      if (phoneInput) phoneInput.value = phone;

      // Consent ticked: add its tag, as the newsletter forms do
      var consent = leadForm.querySelector('[data-consent-tag]');
      var tags = leadForm.querySelector('input[name="contact[tags]"]');
      if (tags) {
        // start from the section's own tags each time (a hidden input keeps changes through form.reset())
        if (!tags.dataset.base) tags.dataset.base = tags.value;
        tags.value = tags.dataset.base;
        if (consent && consent.checked) tags.value += ',' + consent.dataset.consentTag;
      }

      var value = function (key) { var f = leadField(key); return f ? f.value.trim() : ''; };
      var payload = {
        name: value('name'),
        phone: phone,
        email: value('email'),
        city: value('city'),
        consent: consent && consent.checked ? 'Yes' : 'No',
        tags: tags ? tags.value : 'lead',
        page: location.href.split('#')[0]
      };

      leadSending = true;
      setLeadLoading(true);
      sendLeadToSheet(payload).then(function (savedToSheet) {
        if (window.dataLayer) window.dataLayer.push({ event: 'lead_submit', lead_city: payload.city });
        if (leadForm.dataset.shopify === 'true') {
          // Shopify keeps the customer; after the reload the newsletter code shows only this form's
          // message, and the sheet result decides the thank-you if Shopify reports a problem
          try {
            sessionStorage.setItem(NEWSLETTER_KEY, leadForm.id);
            sessionStorage.setItem(LEAD_SHEET_KEY, savedToSheet ? 'saved' : 'failed');
          } catch (err) { /* ignore */ }
          leadReady = true;
          if (leadForm.requestSubmit) leadForm.requestSubmit();
          else if (leadSubmit) leadSubmit.click();
          // Still here after 30s (CAPTCHA script blocked, offline…): let the visitor try again
          setTimeout(function () {
            if (!leadSending) return;
            leadSending = false;
            leadReady = false;
            setLeadLoading(false);
            showLeadMessage(LEAD_FORM_ERRORS.failed);
          }, 30000);
          return;
        }
        leadSending = false;
        setLeadLoading(false);
        if (!savedToSheet && leadForm.dataset.sheetUrl) { showLeadMessage(LEAD_FORM_ERRORS.failed); return; }
        openLead(true);
      });
    });

    window.addEventListener('pageshow', function (e) { if (e.persisted) { leadSending = false; leadReady = false; setLeadLoading(false); } });

    // Back from Shopify after a submit (this form, not a newsletter form):
    // - saved: the thank-you;
    // - Shopify refused the customer (e.g. that phone number is already on another customer)
    //   but the sheet has the lead: still the thank-you, the details are not lost;
    // - neither saved: the form again, with Shopify's message above the button.
    var leadStatus = leadForm.querySelector('[data-lead-status]');
    var leadPosted = submittedFormId === leadForm.id && leadStatus && leadStatus.dataset.leadStatus;
    var leadSheet = null;
    try { leadSheet = sessionStorage.getItem(LEAD_SHEET_KEY); sessionStorage.removeItem(LEAD_SHEET_KEY); } catch (err) { /* ignore */ }
    if (leadPosted === 'posted') openLead(true);
    else if (leadPosted === 'error') {
      if (leadSheet === 'saved') {
        console.warn('[Anera] Lead form: saved to the Google Sheet, but Shopify did not save the customer:', (leadForm.querySelector('.lead-form__message') || {}).textContent);
        openLead(true);
      } else {
        // Shopify's own reason (e.g. "Email is invalid") stays under the tick box; a generic note if it gave none
        if (leadMessage && !leadMessage.textContent.trim()) showLeadMessage(LEAD_FORM_ERRORS.failed);
        else if (leadMessage) leadMessage.hidden = false;
        openLead(false);
      }
    }
    else if (location.hash === LEAD_HASH) openLead(false);
  }

  // ---------- Store locator tabs ----------
  document.querySelectorAll('[data-store-tabs]').forEach(function (tabList) {
    var scope = tabList.closest('[data-store-locator]') || document;
    var tabs = tabList.querySelectorAll('[role="tab"]');
    var cards = scope.querySelectorAll('[data-city]');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) { t.setAttribute('aria-selected', String(t === tab)); });
        var city = tab.dataset.filter;
        cards.forEach(function (card) {
          card.hidden = city !== 'all' && card.dataset.city !== city;
        });
      });
    });
  });

  // ---------- Reveal animations (home page) ----------
  // The head script adds .anera-motion to <html> (not for reduced motion or in the
  // theme editor); CSS then hides these elements until they get .is-revealed here,
  // once, as they scroll into view. Headings are split into words so each line can
  // slide up from behind a mask. The styles live in the "Reveal animations" CSS block.
  var REVEAL_SELECTOR = [
    '.page-hero', '.page-hero__title', '.page-hero__kicker', '.page-hero__eyebrow', '.page-hero__text', '.page-hero .btn',
    '.section-title', '.intention__title',
    '.moments__media', '.moments__body', '.intention__bg',
    '.pieces__hero', '.pieces__thumb', '.pieces__name', '.pieces__desc', '.pieces__detail .btn', '.pieces__head .btn',
    '.intention__content > :not(.intention__title)',
    '.heritage__media', '.heritage__quote', '.heritage__text', '.heritage__body > .btn', '.heritage__body > .link-underline',
    '.split__media', '.split__eyebrow', '.split__text', '.split__body > .btn', '.split__body > .link-underline',
    '.value-card', '.gallery',
    '.occasions__media', '.occasions__eyebrow', '.occasions__list li',
    '.craft__eyebrow', '.craft-card', '.craft__statement',
    '.heading-bar .btn', '.showcase__tile', '.product-tile',
    '.store-feature__media', '.store-feature__desc', '.store-feature__links', '.store-feature__info',
    '.store-tabs', '.store-card',
    '.video-banner__media',
    '.text-cta__eyebrow', '.text-cta__text', '.text-cta .btn',
    '.newsletter__text', '.newsletter__form-wrap',
    '.site-footer__brand', '.site-footer__cols', '.site-footer__bottom'
  ].join(',');
  // Images that open like a curtain (direction picked from where they sit on screen)
  var CURTAIN_SELECTOR = '.moments__media, .pieces__hero, .pieces__thumb, .heritage__media, .store-feature__media, .split__media, .occasions__media';
  var HEADING_SELECTOR = '.page-hero__title, .section-title, .intention__title';
  var root = document.documentElement;

  // Wraps each word in <span class="reveal-word"><span>word</span></span>, keeping <em> etc.
  function splitWords(heading) {
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 1) { walk(child); return; }
        if (child.nodeType !== 3 || !child.textContent.trim()) return;
        var fragment = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (!part.trim()) { fragment.appendChild(document.createTextNode(part)); return; }
          var word = document.createElement('span');
          var inner = document.createElement('span');
          word.className = 'reveal-word';
          inner.textContent = part;
          word.appendChild(inner);
          fragment.appendChild(word);
        });
        node.replaceChild(fragment, child);
      });
    })(heading);
    heading.classList.add('is-split');
  }

  // Numbers the lines (0, 1, 2…) from where each word sits right now, so lines follow one another
  function numberLines(heading) {
    var line = -1;
    var lastTop = null;
    heading.querySelectorAll('.reveal-word').forEach(function (word) {
      var top = word.getBoundingClientRect().top;
      if (lastTop === null || Math.abs(top - lastTop) > 4) { line++; lastTop = top; }
      word.style.setProperty('--anera-reveal-line', line);
    });
  }

  // Left-hand images open to the right, right-hand ones to the left, wide ones from the centre
  function setCurtainDirection(el) {
    var rect = el.getBoundingClientRect();
    var viewportWidth = document.documentElement.clientWidth;
    var from = 'left';
    if (rect.width > viewportWidth * .7) from = 'center';
    else if (rect.left + rect.width / 2 > viewportWidth / 2) from = 'right';
    el.setAttribute('data-reveal-from', from);
  }

  function reveal(el) {
    if (el.classList.contains('is-split')) numberLines(el);
    if (el.matches(CURTAIN_SELECTOR)) setCurtainDirection(el);
    el.classList.add('is-revealed');
  }

  // Shown straight away, no animation (e.g. already scrolled past on reload)
  function revealInstantly(el) {
    el.classList.add('is-revealed', 'is-revealed-instant');
  }

  // The hero and its text reveal together, straight away. With a background video the
  // poster image shows first; it fades to the video once that is playing (see above).
  function revealHero(hero) {
    reveal(hero);
    hero.querySelectorAll(REVEAL_SELECTOR).forEach(reveal);
  }

  if (root.classList.contains('anera-motion')) {
    window.aneraMotionReady = true; // tells the head script's safety timer that we're running

    // Announcement bar slides down, then the header intro (every page): menu items,
    // then the logo, then the icons, one after another
    document.querySelectorAll('.announcement').forEach(function (bar) { bar.classList.add('is-intro'); });
    if (header) {
      var introIndex = 0;
      header.querySelectorAll('.main-nav__item, .site-header__logo, .site-header__actions > *').forEach(function (item) {
        if (item.offsetParent === null && getComputedStyle(item).position !== 'fixed') return; // hidden (e.g. desktop menu on mobile)
        item.style.setProperty('--anera-intro-index', introIndex++);
      });
      header.classList.add('is-intro');
    }

    if (document.body.classList.contains('anera') && 'IntersectionObserver' in window) {
      var allRevealItems = Array.prototype.slice.call(document.querySelectorAll(REVEAL_SELECTOR));
      allRevealItems.forEach(function (el) { if (el.matches(HEADING_SELECTOR)) splitWords(el); });
      // The hero's text is revealed along with the hero (see revealHero)
      var revealItems = allRevealItems.filter(function (el) {
        return el.classList.contains('page-hero') || !el.closest('.page-hero');
      });

      var revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealObserver.unobserve(entry.target);
          if (entry.target.classList.contains('page-hero')) revealHero(entry.target);
          else reveal(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });

      var startReveals = function () {
        revealItems.forEach(function (el) {
          // Already scrolled past (e.g. reload halfway down): show without animating
          if (el.getBoundingClientRect().bottom < 0) {
            revealInstantly(el);
            if (el.classList.contains('page-hero')) el.querySelectorAll(REVEAL_SELECTOR).forEach(revealInstantly);
          } else {
            revealObserver.observe(el);
          }
        });
      };
      // Wait briefly for the web fonts so heading lines are measured with the final font
      if (document.fonts && document.fonts.ready) {
        var started = false;
        var startOnce = function () { if (!started) { started = true; startReveals(); } };
        document.fonts.ready.then(startOnce);
        setTimeout(startOnce, 600);
      } else {
        startReveals();
      }

      // Image banner parallax (desktop): the photo drifts a little slower than the page
      var parallaxBanners = document.querySelectorAll('.intention');
      var desktop = window.matchMedia('(min-width: 901px)');
      var parallaxTicking = false;
      var updateParallax = function () {
        parallaxTicking = false;
        var viewportHeight = window.innerHeight;
        parallaxBanners.forEach(function (banner) {
          var bg = banner.querySelector('.intention__bg');
          if (!bg) return;
          if (!desktop.matches) { banner.style.removeProperty('--anera-parallax-y'); return; }
          var rect = banner.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > viewportHeight) return;
          // -1 when the banner enters at the bottom, 1 when it leaves at the top
          var progress = (viewportHeight / 2 - (rect.top + rect.height / 2)) / (viewportHeight / 2 + rect.height / 2);
          // set on the banner so the image and a background video move as one
          banner.style.setProperty('--anera-parallax-y', (progress * rect.height * .1).toFixed(1) + 'px');
        });
      };
      var requestParallax = function () {
        if (!parallaxTicking) { parallaxTicking = true; requestAnimationFrame(updateParallax); }
      };
      if (parallaxBanners.length) {
        window.addEventListener('scroll', requestParallax, { passive: true });
        window.addEventListener('resize', requestParallax);
        updateParallax();
      }
    }
  }
})();
